import { api } from "@breakout/backend/convex/_generated/api"
import { Button } from "@breakout/ui/components/Button"
import { useToast } from "@breakout/ui/components/Toast"
import { convexQuery } from "@convex-dev/react-query"
import { usePrivy } from "@privy-io/react-auth"
import * as Sentry from "@sentry/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useMutation as useConvexMutation } from "convex/react"
import {
	ArrowLeft,
	Camera,
	CheckCircle,
	Loader2,
	Twitter,
	Upload,
	User,
	XCircle,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { PageContainer } from "../../components/layout/PageContainer"
import { useDocumentTitle } from "../../hooks/common/useDocumentTitle"
import { useTabNavigation } from "../../hooks/common/useTabNavigation"
import { useUploadAvatar } from "../../hooks/user/useUploadAvatar"

export const Route = createFileRoute("/profile/edit")({
	component: EditProfilePage,
})

function EditProfilePage() {
	useDocumentTitle("Edit Profile")

	// 1. Routing params
	const router = useRouter()

	// 2. Context/store access
	const { navigateWithToast } = useTabNavigation()
	const queryClient = useQueryClient()
	const { ready, authenticated } = usePrivy()
	const { showToast } = useToast()
	const fileInputRef = useRef<HTMLInputElement>(null)

	// Form state
	const [username, setUsername] = useState("")
	const [avatarUrl, setAvatarUrl] = useState("")
	const [hasChanges, setHasChanges] = useState(false)
	const [debouncedUsername, setDebouncedUsername] = useState("")
	const [isSaving, setIsSaving] = useState(false)

	const { uploadAvatar, isUploading, error: uploadError } = useUploadAvatar()
	const updateProfile = useConvexMutation(api.domains.user.profile.updateProfile)

	// 3. Data queries
	const { data: convexUser, refetch: refetchUser } = useQuery({
		...convexQuery(api.domains.user.auth.query.current, {}),
		enabled: authenticated,
	})

	const { data: usernameCheck, isLoading: isCheckingUsername } = useQuery({
		...convexQuery(api.domains.user.profile.checkUsernameAvailable, {
			username: debouncedUsername,
		}),
		enabled: debouncedUsername.length >= 3,
	})

	// 4. Effects
	// Initialize form from Convex data
	useEffect(() => {
		if (convexUser) {
			setUsername(convexUser.username)
			setAvatarUrl(convexUser.avatarUrl)
		}
	}, [convexUser])

	// Debounce username for availability check
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedUsername(username)
		}, 300)
		return () => clearTimeout(timer)
	}, [username])

	// Track changes
	useEffect(() => {
		if (!convexUser) {
			return
		}
		const changed = username !== convexUser.username || avatarUrl !== convexUser.avatarUrl
		setHasChanges(changed)
	}, [username, avatarUrl, convexUser])

	// 5. Derived values
	// Username validation state
	const showUsernameStatus = username.length >= 3 && convexUser && username !== convexUser.username
	const isUsernameValid = usernameCheck?.available === true
	const usernameError = usernameCheck?.error

	// Disable save if username is invalid
	const canSave =
		hasChanges &&
		!isSaving &&
		(username.length === 0 || username.length >= 3) &&
		(!showUsernameStatus || isUsernameValid)

	// 6. Event handlers
	const handleSave = useCallback(async () => {
		if (!convexUser) {
			return
		}

		// Validate username if changed
		if (username && username !== convexUser.username) {
			if (username.length < 3) {
				showToast("Username must be at least 3 characters", "error")
				return
			}
			if (usernameCheck && !usernameCheck.available) {
				showToast(usernameCheck.error || "Username is not available", "error")
				return
			}
		}

		setIsSaving(true)
		try {
			const result = await updateProfile({
				username: username.trim() || undefined,
				avatarUrl: avatarUrl || undefined,
			})

			if (!result.success) {
				showToast(result.error || "Failed to update profile", "error")
				return
			}

			await refetchUser()
			// Invalidate analytics queries so TopHolders/ActivityFeed show updated profile
			queryClient.invalidateQueries({ queryKey: ["analytics"] })
			showToast("Profile updated", "success")
			navigateWithToast({ to: "/profile" })
		} catch (error) {
			showToast("Failed to update profile", "error")

			// Log profile update failures to Sentry
			Sentry.captureException(error, {
				tags: { operation: "updateProfile", domain: "profile" },
				extra: {
					hasUsernameChange: username !== convexUser?.username,
					hasAvatarChange: avatarUrl !== convexUser?.avatarUrl,
				},
			})
		} finally {
			setIsSaving(false)
		}
	}, [
		username,
		avatarUrl,
		convexUser,
		usernameCheck,
		updateProfile,
		refetchUser,
		queryClient,
		showToast,
		navigateWithToast,
	])

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) {
			return
		}
		if (!file.type.startsWith("image/")) {
			showToast("Please select an image file", "error")
			return
		}

		try {
			const result = await uploadAvatar(file)
			if (result?.url) {
				setAvatarUrl(result.url)
			}
		} catch {
			showToast("Failed to upload image", "error")
		}
	}

	// Show loading skeleton while checking auth
	if (!ready) {
		return (
			<>
				{/* Header - show static content */}
				<div className="border-border-subtle border-b bg-bg-card">
					<PageContainer className="py-4">
						<div className="flex items-center gap-4">
							<div className="flex size-10 items-center justify-center rounded-full bg-bg-tertiary">
								<ArrowLeft aria-hidden="true" className="size-5 text-text-muted" />
							</div>
							<h1 className="font-display font-semibold text-text-primary text-xl">Edit Profile</h1>
						</div>
					</PageContainer>
				</div>
				<PageContainer className="pt-6 pb-8 sm:pt-8">
					<div className="mx-auto max-w-md">
						{/* Avatar skeleton */}
						<div className="mb-8 flex flex-col items-center">
							<div className="relative">
								<div className="size-28 animate-pulse rounded-full bg-bg-tertiary sm:size-32" />
								<div className="absolute -right-1 -bottom-1 flex size-10 items-center justify-center rounded-full border-4 border-bg-primary bg-bg-tertiary">
									<Camera aria-hidden="true" className="size-5 text-text-muted" />
								</div>
							</div>
							<p className="mt-3 text-text-secondary">Tap to change photo</p>
						</div>
						{/* Form card skeleton */}
						<div className="space-y-6 rounded-2xl border border-border-subtle bg-bg-card p-6 shadow-sm">
							{/* Username field - show label */}
							<div>
								<span className="mb-2 block font-medium text-text-secondary">Username</span>
								<div className="h-[54px] w-full animate-pulse rounded-xl bg-bg-tertiary" />
							</div>
							{/* Twitter section skeleton */}
							<div className="h-[76px] w-full animate-pulse rounded-xl bg-bg-tertiary" />
						</div>
						{/* Save button - show static text */}
						<div className="mt-6 sm:mt-8">
							<div className="flex h-[52px] w-full items-center justify-center rounded-xl bg-bg-tertiary">
								<span className="font-medium text-lg text-text-muted">Save Changes</span>
							</div>
						</div>
						{/* Cancel link */}
						<div className="mt-4 text-center">
							<span className="text-text-muted">Cancel</span>
						</div>
					</div>
				</PageContainer>
			</>
		)
	}

	if (!authenticated) {
		router.navigate({ to: "/" })
		return null
	}

	return (
		<>
			{/* Header */}
			<div className="border-border-subtle border-b bg-bg-card">
				<PageContainer className="py-4">
					<div className="flex items-center gap-4">
						<Link
							className="flex size-10 items-center justify-center rounded-full transition-colors hover:bg-bg-tertiary"
							to="/profile"
						>
							<ArrowLeft aria-hidden="true" className="size-5 text-text-secondary" />
							<span className="sr-only">Back to profile</span>
						</Link>
						<h1 className="font-display font-semibold text-text-primary text-xl">Edit Profile</h1>
					</div>
				</PageContainer>
			</div>

			<PageContainer className="pt-6 pb-8 sm:pt-8">
				<div className="mx-auto max-w-md">
					{/* Hidden file input */}
					<input
						accept="image/*"
						aria-label="Upload profile picture"
						className="hidden"
						onChange={handleFileChange}
						ref={fileInputRef}
						type="file"
					/>

					{/* Avatar Section */}
					<div className="mb-8 flex flex-col items-center">
						<button
							aria-label="Change profile picture"
							className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2"
							disabled={isUploading}
							onClick={() => fileInputRef.current?.click()}
							type="button"
						>
							<div className="flex size-28 items-center justify-center overflow-hidden rounded-full bg-bg-tertiary sm:size-32">
								{isUploading ? (
									<Loader2 aria-hidden="true" className="size-10 animate-spin text-text-muted" />
								) : avatarUrl ? (
									<img
										alt="Avatar"
										className="size-full object-cover"
										decoding="async"
										height={128}
										referrerPolicy="no-referrer"
										src={avatarUrl}
										width={128}
									/>
								) : (
									<User aria-hidden="true" className="size-14 text-white sm:size-16" />
								)}
							</div>
							{!isUploading && (
								<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
									<Upload aria-hidden="true" className="size-8 text-white" />
								</div>
							)}
							<div className="absolute -right-1 -bottom-1 flex size-10 items-center justify-center rounded-full border-4 border-gray-50 bg-gray-900">
								<Camera aria-hidden="true" className="size-5 text-white" />
							</div>
						</button>
						<p className="mt-3 text-gray-500">
							{isUploading ? "Optimizing & uploading..." : "Tap to change photo"}
						</p>
						{uploadError && <p className="mt-1 text-red-500 text-sm">{uploadError.message}</p>}
					</div>

					{/* Form Card */}
					<div className="space-y-6 rounded-2xl border border-border-subtle bg-bg-card p-6 shadow-sm">
						{/* Username */}
						<div>
							<label className="mb-2 block font-medium text-text-secondary" htmlFor="username">
								Username
							</label>
							<div className="relative">
								<span className="absolute top-1/2 left-4 -translate-y-1/2 text-text-muted">@</span>
								<input
									autoComplete="username"
									className={`w-full rounded-xl border bg-bg-secondary py-3.5 pr-12 pl-9 text-text-primary placeholder-text-muted transition-colors focus:border-transparent focus:bg-bg-card focus:outline-none focus:ring-2 ${
										showUsernameStatus && !isCheckingUsername
											? isUsernameValid
												? "border-green-500/50 focus:ring-green-500"
												: "border-red-500/50 focus:ring-red-500"
											: "border-border-highlight focus:ring-text-muted"
									}`}
									id="username"
									maxLength={20}
									onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
									placeholder="username…"
									type="text"
									value={username}
								/>
								{showUsernameStatus && (
									<div className="absolute top-1/2 right-4 -translate-y-1/2">
										{isCheckingUsername ? (
											<Loader2 aria-hidden="true" className="size-5 animate-spin text-text-muted" />
										) : isUsernameValid ? (
											<CheckCircle aria-hidden="true" className="size-5 text-green-500" />
										) : (
											<XCircle aria-hidden="true" className="size-5 text-red-500" />
										)}
									</div>
								)}
							</div>
							{showUsernameStatus && !isCheckingUsername && usernameError && (
								<p className="mt-2 text-red-500 text-sm">{usernameError}</p>
							)}
							{username.length > 0 && username.length < 3 && (
								<p className="mt-2 text-sm text-text-muted">
									Username must be at least 3 characters
								</p>
							)}
						</div>

						{/* Connect X / Twitter - Coming Soon */}
						<div className="rounded-xl bg-bg-secondary p-4 opacity-70">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="flex size-11 items-center justify-center rounded-full bg-black">
										<Twitter aria-hidden="true" className="size-5 text-white" />
									</div>
									<div>
										<p className="font-medium text-text-primary">Connect X</p>
										<p className="text-sm text-text-muted">Coming soon</p>
									</div>
								</div>
								<span className="rounded-full bg-bg-tertiary px-3 py-1.5 font-medium text-text-muted text-xs">
									Soon
								</span>
							</div>
						</div>
					</div>

					{/* Save Button */}
					<div className="mt-6 sm:mt-8">
						<Button
							className="w-full"
							disabled={!canSave}
							loading={isSaving}
							onClick={handleSave}
							size="lg"
						>
							Save Changes
						</Button>
					</div>

					{/* Cancel link */}
					<div className="mt-4 text-center">
						<Link className="text-text-muted hover:text-text-primary hover:underline" to="/profile">
							Cancel
						</Link>
					</div>
				</div>
			</PageContainer>
		</>
	)
}
